import { useEffect, useMemo, useState } from "react";
import {
  api,
  ApiError,
  type AdminConsultationForm,
  type ConsultationField,
  type ConsultationFieldKind,
} from "../lib/api.ts";
import {
  ConsultationFields,
  initialAnswers,
  isContentKind,
  type AnswerMap,
} from "../consultation/ConsultationFields.tsx";

interface ConsultationFormEditorProps {
  token: string;
  onUnauthorised: () => void;
}

/**
 * The question types, in the order they are offered.
 *
 * Named for what the studio is trying to ask rather than for the control that
 * results - "Choose any" rather than "checkbox group", because the person
 * building the form is thinking about the question.
 */
const KINDS: Array<{
  kind: ConsultationFieldKind;
  label: string;
  note?: string;
}> = [
  { kind: "text", label: "Short answer" },
  { kind: "textarea", label: "Long answer" },
  { kind: "yesno", label: "Yes or no" },
  {
    kind: "choice",
    label: "Choose one",
    note: "One option per line, at least two.",
  },
  {
    kind: "multi",
    label: "Choose any",
    note: "One option per line, at least two.",
  },
  {
    kind: "scale",
    label: "Scale of 1 to 5",
    note: "Optionally two labels: the low end, then the high end.",
  },
  { kind: "date", label: "Date" },
  { kind: "number", label: "Number" },
  { kind: "email", label: "Email address" },
  { kind: "tel", label: "Phone number" },
  {
    kind: "consent",
    label: "Tick to agree",
    note: "The wording is what they are agreeing to.",
  },
  { kind: "heading", label: "Section heading" },
  { kind: "statement", label: "Note to read" },
];

const KIND_LABEL = new Map(KINDS.map((entry) => [entry.kind, entry.label]));
const KIND_NOTE = new Map(KINDS.map((entry) => [entry.kind, entry.note]));

function usesOptions(kind: ConsultationFieldKind): boolean {
  return kind === "choice" || kind === "multi" || kind === "scale";
}

/**
 * A question while it is being edited.
 *
 * `key` is local and only for React - a new question has no id until it is
 * saved, and using the array index instead would make every reorder retype the
 * inputs. `options` is held as raw text because that is how it is edited; it is
 * split into lines on the way out.
 */
interface DraftField {
  key: string;
  id?: number;
  kind: ConsultationFieldKind;
  label: string;
  helpText: string;
  options: string;
  isRequired: boolean;
}

let nextKey = 1;

function toDraft(field: ConsultationField): DraftField {
  return {
    key: `f${nextKey++}`,
    id: field.id,
    kind: field.kind,
    label: field.label,
    helpText: field.helpText ?? "",
    options: field.options.join("\n"),
    isRequired: field.isRequired,
  };
}

function optionLines(options: string): string[] {
  return options
    .split("\n")
    .map((option) => option.trim())
    .filter((option) => option.length > 0);
}

/** Compared as a string, which is the cheapest honest way to spot a change. */
function fingerprint(
  title: string,
  intro: string,
  fields: DraftField[],
): string {
  return JSON.stringify([
    title.trim(),
    intro.trim(),
    fields.map((field) => [
      field.id ?? null,
      field.kind,
      field.label.trim(),
      field.helpText.trim(),
      usesOptions(field.kind) ? optionLines(field.options) : [],
      isContentKind(field.kind) ? false : field.isRequired,
    ]),
  ]);
}

export function ConsultationFormEditor({
  token,
  onUnauthorised,
}: ConsultationFormEditorProps) {
  const [loaded, setLoaded] = useState<AdminConsultationForm | null>(null);
  const [title, setTitle] = useState("");
  const [intro, setIntro] = useState("");
  const [fields, setFields] = useState<DraftField[]>([]);
  const [adding, setAdding] = useState<ConsultationFieldKind>("textarea");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState<AnswerMap>({});

  function adopt(form: AdminConsultationForm) {
    setLoaded(form);
    setTitle(form.title);
    setIntro(form.intro ?? "");
    setFields(form.fields.map(toDraft));
  }

  useEffect(() => {
    api.admin
      .getConsultationForm(token)
      .then(adopt)
      .catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.status === 401)
          return onUnauthorised();
        setError("The form could not be loaded.");
      });
  }, [token, onUnauthorised]);

  const dirty = useMemo(() => {
    if (!loaded) return false;
    return (
      fingerprint(title, intro, fields) !==
      fingerprint(loaded.title, loaded.intro ?? "", loaded.fields.map(toDraft))
    );
  }, [loaded, title, intro, fields]);

  /**
   * Problems worth blocking a save for, in the studio's words.
   *
   * The API checks all of this too - it has to, since it is the only thing a
   * stale tab cannot get round - but being told before pressing Save is much
   * better than being told after.
   */
  const problems = useMemo(() => {
    const found: string[] = [];
    if (title.trim().length < 2) found.push("The form needs a title.");
    fields.forEach((field, index) => {
      const position = index + 1;
      if (field.label.trim().length === 0) {
        found.push(`Question ${position} has no wording yet.`);
      }
      if (
        (field.kind === "choice" || field.kind === "multi") &&
        optionLines(field.options).length < 2
      ) {
        found.push(
          `Question ${position} is a choice, so it needs at least two options.`,
        );
      }
      if (field.kind === "scale") {
        const count = optionLines(field.options).length;
        if (count !== 0 && count !== 2) {
          found.push(
            `Question ${position} is a scale: give it either no labels or exactly two.`,
          );
        }
      }
    });
    return found;
  }, [title, fields]);

  const questionCount = fields.filter(
    (field) => !isContentKind(field.kind),
  ).length;

  function update(key: string, changes: Partial<DraftField>) {
    setFields((current) =>
      current.map((field) =>
        field.key === key ? { ...field, ...changes } : field,
      ),
    );
    setMessage(null);
  }

  function move(index: number, delta: number) {
    setFields((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved!);
      return next;
    });
    setMessage(null);
  }

  function add(kind: ConsultationFieldKind) {
    setFields((current) => [
      ...current,
      {
        key: `f${nextKey++}`,
        kind,
        label: "",
        helpText: "",
        options: "",
        isRequired: false,
      },
    ]);
    setMessage(null);
  }

  function duplicate(index: number) {
    setFields((current) => {
      const source = current[index];
      if (!source) return current;
      const next = [...current];
      // Without an id, so it saves as a new question rather than overwriting the
      // one it was copied from.
      next.splice(index + 1, 0, {
        ...source,
        key: `f${nextKey++}`,
        id: undefined,
      });
      return next;
    });
    setMessage(null);
  }

  function remove(index: number) {
    const field = fields[index];
    if (!field) return;
    // Only worth warning about a question that exists on the live form; a new
    // one that has never been saved can have nothing filed against it.
    if (
      field.id !== undefined &&
      !window.confirm(
        `Remove "${field.label.slice(0, 60) || "this question"}"?\n\n` +
          "Answers already given to it are kept and stay readable against the " +
          "wording they were asked under. Nobody will be asked it again.",
      )
    ) {
      return;
    }
    setFields((current) => current.filter((entry) => entry.key !== field.key));
    setMessage(null);
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.admin.saveConsultationForm(token, {
        title: title.trim(),
        intro: intro.trim() || null,
        fields: fields.map((field) => ({
          id: field.id,
          kind: field.kind,
          label: field.label.trim(),
          helpText: field.helpText.trim() || null,
          options: usesOptions(field.kind) ? optionLines(field.options) : [],
          isRequired: isContentKind(field.kind) ? false : field.isRequired,
        })),
      });
      adopt(saved);
      setMessage(
        `Saved. Clients now see ${saved.questionCount} question${saved.questionCount === 1 ? "" : "s"}.`,
      );
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The form could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) {
    return error ? (
      <p className="notice notice--error">{error}</p>
    ) : (
      <p className="booking__loading">
        <span className="spinner" /> Loading the form…
      </p>
    );
  }

  // The preview renders through the very component the client uses, so what is
  // checked here is what they will see.
  const previewFields: ConsultationField[] = fields
    .filter((field) => field.label.trim().length > 0)
    .map((field, index) => ({
      id: index + 1,
      kind: field.kind,
      label: field.label.trim(),
      helpText: field.helpText.trim() || null,
      options: usesOptions(field.kind) ? optionLines(field.options) : [],
      isRequired: isContentKind(field.kind) ? false : field.isRequired,
      sortOrder: index,
    }));

  return (
    <div className="stack-lg">
      <div>
        <h2 className="admin__section-title">The consultation form</h2>
        <p className="field-hint">
          What every client is asked after they book. Changes apply to the next
          person who opens their form; answers already given keep the wording
          they were asked under, so rewording a question never rewrites
          somebody's history.
        </p>
      </div>

      <div className="field">
        <label htmlFor="cform-title">Form title</label>
        <input
          id="cform-title"
          className="input"
          maxLength={160}
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
            setMessage(null);
          }}
        />
      </div>

      <div className="field">
        <label htmlFor="cform-intro">Introduction</label>
        <textarea
          id="cform-intro"
          className="textarea"
          rows={3}
          maxLength={2000}
          value={intro}
          onChange={(event) => {
            setIntro(event.target.value);
            setMessage(null);
          }}
        />
        <p className="field-hint">
          Read before anything is answered, so it is the place to say why you
          are asking.
        </p>
      </div>

      <div className="builder__head">
        <h3 className="content-section__title">
          {fields.length} item{fields.length === 1 ? "" : "s"}, {questionCount}{" "}
          question
          {questionCount === 1 ? "" : "s"}
        </h3>
        <button
          type="button"
          className="btn btn--quiet"
          aria-pressed={preview}
          onClick={() => {
            setPreview((open) => !open);
            if (!preview) setPreviewAnswers(initialAnswers(previewFields));
          }}
        >
          {preview ? "Hide preview" : "Preview"}
        </button>
      </div>

      {preview && (
        <section className="panel builder__preview">
          <p className="label">As the client sees it</p>
          <h4 className="cform__summary-title">{title}</h4>
          {intro.trim() && <p className="cform__intro">{intro}</p>}
          <ConsultationFields
            fields={previewFields}
            answers={previewAnswers}
            onChange={(fieldId, values) =>
              setPreviewAnswers((current) => ({
                ...current,
                [fieldId]: values,
              }))
            }
          />
        </section>
      )}

      <ol className="builder">
        {fields.map((field, index) => {
          const note = KIND_NOTE.get(field.kind);
          const content = isContentKind(field.kind);

          return (
            <li
              key={field.key}
              className={`builder-item builder-item--${content ? "content" : "question"}`}
            >
              <div className="builder-item__bar">
                <span className="builder-item__position">
                  {content ? "-" : String(index + 1).padStart(2, "0")}
                </span>

                <label
                  className="visually-hidden"
                  htmlFor={`kind-${field.key}`}
                >
                  Question type for item {index + 1}
                </label>
                <select
                  id={`kind-${field.key}`}
                  className="select select--inline"
                  value={field.kind}
                  onChange={(event) =>
                    update(field.key, {
                      kind: event.target.value as ConsultationFieldKind,
                    })
                  }
                >
                  {KINDS.map((entry) => (
                    <option key={entry.kind} value={entry.kind}>
                      {entry.label}
                    </option>
                  ))}
                </select>

                <div className="builder-item__actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--icon"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${KIND_LABEL.get(field.kind)} up`}
                  >
                    &#8593;
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--icon"
                    onClick={() => move(index, 1)}
                    disabled={index === fields.length - 1}
                    aria-label={`Move ${KIND_LABEL.get(field.kind)} down`}
                  >
                    &#8595;
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet"
                    onClick={() => duplicate(index)}
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet builder-item__remove"
                    onClick={() => remove(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="field">
                <label htmlFor={`label-${field.key}`}>
                  {content ? "Wording" : "Question"}
                </label>
                <textarea
                  id={`label-${field.key}`}
                  className="textarea"
                  rows={content || field.kind === "consent" ? 3 : 2}
                  maxLength={300}
                  value={field.label}
                  aria-invalid={field.label.trim().length === 0}
                  onChange={(event) =>
                    update(field.key, { label: event.target.value })
                  }
                />
                {field.label.trim().length === 0 && (
                  <p className="field-error">This needs some wording.</p>
                )}
              </div>

              {!content && (
                <div className="field">
                  <label htmlFor={`help-${field.key}`}>Help text</label>
                  <input
                    id={`help-${field.key}`}
                    className="input"
                    maxLength={600}
                    placeholder="Optional. Shown in small type under the question."
                    value={field.helpText}
                    onChange={(event) =>
                      update(field.key, { helpText: event.target.value })
                    }
                  />
                </div>
              )}

              {usesOptions(field.kind) && (
                <div className="field">
                  <label htmlFor={`options-${field.key}`}>
                    {field.kind === "scale" ? "End labels" : "Options"}
                  </label>
                  <textarea
                    id={`options-${field.key}`}
                    className="textarea"
                    rows={field.kind === "scale" ? 2 : 5}
                    value={field.options}
                    onChange={(event) =>
                      update(field.key, { options: event.target.value })
                    }
                  />
                  {note && <p className="field-hint">{note}</p>}
                </div>
              )}

              {!content && (
                <label
                  className="builder-item__required"
                  htmlFor={`required-${field.key}`}
                >
                  <input
                    id={`required-${field.key}`}
                    type="checkbox"
                    checked={field.isRequired}
                    onChange={(event) =>
                      update(field.key, { isRequired: event.target.checked })
                    }
                  />
                  <span>
                    {field.kind === "consent"
                      ? "Must be ticked before the form can be sent"
                      : "Must be answered before the form can be sent"}
                  </span>
                </label>
              )}
            </li>
          );
        })}
      </ol>

      <div className="builder__add row">
        <label className="visually-hidden" htmlFor="cform-add-kind">
          Type of item to add
        </label>
        <select
          id="cform-add-kind"
          className="select"
          value={adding}
          onChange={(event) =>
            setAdding(event.target.value as ConsultationFieldKind)
          }
        >
          {KINDS.map((entry) => (
            <option key={entry.kind} value={entry.kind}>
              {entry.label}
            </option>
          ))}
        </select>
        <button type="button" className="btn" onClick={() => add(adding)}>
          Add
        </button>
      </div>

      {problems.length > 0 && (
        <ul className="admin-form__errors">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      {message && <p className="notice notice--success">{message}</p>}
      {error && <p className="notice notice--error">{error}</p>}

      <div className="row admin__sticky-actions">
        <button
          type="button"
          className="btn"
          onClick={save}
          disabled={busy || !dirty || problems.length > 0}
        >
          {busy ? (
            <span className="spinner" />
          ) : dirty ? (
            "Save the form"
          ) : (
            "No changes"
          )}
        </button>
        {dirty && (
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              adopt(loaded);
              setMessage(null);
              setError(null);
            }}
          >
            Discard changes
          </button>
        )}
      </div>
    </div>
  );
}
